import { useState, useEffect } from 'react';
import { trpc } from '../../../lib/trpc.js';
import { getStoredUser } from '../../../lib/auth.js';
import { RuleForm } from './RuleForm.js';

type RuleType = 'campaign_roas' | 'account_roas' | 'morning_reenable' | 'geo' | 'custom';

interface GuardianRule {
  id: string;
  tenantId: string;
  name: string;
  type: string;
  conditions: Record<string, unknown>;
  actions: Record<string, unknown>;
  mode: string;
  enabled: boolean | null;
  createdAt: Date | string;
  updatedAt: Date | string;
}

const TYPE_BADGE: Record<string, { label: string; cls: string }> = {
  campaign_roas:    { label: 'campaign_roas',    cls: 'bg-blue-500/15 text-blue-400 border-blue-500/20' },
  account_roas:     { label: 'account_roas',     cls: 'bg-purple-500/15 text-purple-400 border-purple-500/20' },
  morning_reenable: { label: 'morning_reenable', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/20' },
  geo:              { label: 'geo',              cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' },
  custom:           { label: 'custom',           cls: 'bg-slate-500/15 text-slate-400 border-slate-500/20' },
};

function formatTimeAgo(date: Date | string | null): string {
  if (!date) return 'Never';
  const d = typeof date === 'string' ? new Date(date) : date;
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      onClick={onChange}
      className={`relative w-10 rounded-full transition-colors ${checked ? 'bg-emerald-500' : 'bg-slate-700'}`}
      style={{ minWidth: '2.5rem', height: '1.375rem' }}
    >
      <span
        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`}
      />
    </button>
  );
}

function Skeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      {[1, 2, 3].map(i => (
        <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl p-5 h-28" />
      ))}
    </div>
  );
}

export function RulesList() {
  const [ruleList, setRuleList] = useState<GuardianRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [tenantId, setTenantId] = useState('');

  function loadRules(tid: string) {
    trpc.guardian.listRules.query({ tenantId: tid })
      .then(data => setRuleList(data as GuardianRule[]))
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    const user = getStoredUser();
    if (!user) { setLoading(false); return; }
    setTenantId(user.tenantId);
    loadRules(user.tenantId);
  }, []);

  function toggleEnabled(id: string) {
    setRuleList(prev => prev.map(r => r.id === id ? { ...r, enabled: !r.enabled } : r));
  }

  function toggleMode(id: string) {
    setRuleList(prev => prev.map(r =>
      r.id === id ? { ...r, mode: r.mode === 'active' ? 'monitor' : 'active' } : r
    ));
  }

  function deleteRule(id: string) {
    setRuleList(prev => prev.filter(r => r.id !== id));
  }

  function handleRuleCreated() {
    setShowForm(false);
    if (tenantId) loadRules(tenantId);
  }

  if (loading) return <Skeleton />;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{ruleList.filter(r => r.enabled).length} active rules</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-semibold text-white transition-colors"
        >
          <span>+</span> Create Rule
        </button>
      </div>

      {/* Create Form */}
      {showForm && (
        <RuleForm
          tenantId={tenantId}
          onClose={() => setShowForm(false)}
          onCreated={handleRuleCreated}
        />
      )}

      {ruleList.length === 0 && !showForm && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-10 text-center">
          <p className="text-3xl mb-2">🛡️</p>
          <p className="text-slate-300 font-medium text-sm">No rules yet</p>
          <p className="text-slate-500 text-xs mt-1">Create your first Guardian rule to get started.</p>
        </div>
      )}

      {/* Rule Cards */}
      <div className="space-y-3">
        {ruleList.map(rule => {
          const badge = TYPE_BADGE[rule.type] ?? TYPE_BADGE.custom;
          return (
            <div
              key={rule.id}
              className={`bg-slate-900 border rounded-xl p-5 transition-opacity ${
                rule.enabled ? 'border-slate-800' : 'border-slate-800 opacity-60'
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Main content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="font-bold text-slate-100">{rule.name}</span>
                    <span className={`text-xs font-mono px-2 py-0.5 rounded border ${badge.cls}`}>
                      {badge.label}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 mb-3">
                    <div className="flex items-start gap-2 text-sm">
                      <span className="text-slate-600 flex-shrink-0 mt-0.5">If</span>
                      <span className="text-slate-300 font-mono text-xs">{JSON.stringify(rule.conditions).slice(0, 80)}</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm">
                      <span className="text-slate-600 flex-shrink-0 mt-0.5">Then</span>
                      <span className="text-slate-300 font-mono text-xs">{JSON.stringify(rule.actions).slice(0, 80)}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-5 text-xs text-slate-500">
                    <span>Created <span className="text-slate-300 font-medium">{formatTimeAgo(rule.createdAt)}</span></span>
                    <span>Updated <span className="text-slate-300 font-medium">{formatTimeAgo(rule.updatedAt)}</span></span>
                  </div>
                </div>

                {/* Controls */}
                <div className="flex flex-col items-end gap-3 flex-shrink-0">
                  {/* Mode toggle */}
                  <button
                    onClick={() => toggleMode(rule.id)}
                    className={`text-xs font-semibold px-3 py-1 rounded-md border transition-colors ${
                      rule.mode === 'active'
                        ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                        : 'border-amber-500/40 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'
                    }`}
                  >
                    {rule.mode === 'active' ? '⚡ Active' : '👁️ Monitor'}
                  </button>

                  {/* Enable toggle */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">{rule.enabled ? 'On' : 'Off'}</span>
                    <Toggle checked={!!rule.enabled} onChange={() => toggleEnabled(rule.id)} />
                  </div>

                  {/* Delete */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => deleteRule(rule.id)}
                      className="text-xs text-slate-500 hover:text-rose-400 transition-colors px-2 py-1 rounded hover:bg-slate-800"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
